import React, { useState } from 'react';
import { UserProfile } from '../../../types';
import { supabase } from '../../../lib/supabase';
import { LayoutDashboard, Save, Users, Loader2, Trash2, CheckCircle2 } from 'lucide-react';

// Platform Imports
import { ShopifySettings } from './platforms/Shopify';
import { WooCommerceSettings } from './platforms/WooCommerce';
import { BigCommerceSettings } from './platforms/BigCommerce';
import { GenericPlatformSettings } from './platforms/GenericPlatform';

// WhatsApp Imports
import { InteraktSettings } from './communications/Interakt';
import { WatiSettings } from './communications/Wati';
import { AiSensySettings } from './communications/AiSensy';
import { BitespeedSettings } from './communications/Bitespeed';
import { GenericWhatsappSettings } from './communications/GenericWhatsapp';

// Storage Imports
import { GoogleDriveSettings } from './storage/GoogleDrive';

// Password Imports 
import { SecuritySettings } from './SecuritySettings';

// --- CONFIGURATION MAPS ---
const PLATFORMS: Record<string, React.FC<any>> = {
  'Shopify': ShopifySettings,
  'WooCommerce': WooCommerceSettings,
  'BigCommerce': BigCommerceSettings,
  'Other': GenericPlatformSettings
};

const WHATSAPP_PROVIDERS: Record<string, React.FC<any>> = {
  'Interakt': InteraktSettings,
  'Wati': WatiSettings,
  'AiSensy': AiSensySettings,
  'Bitespeed': BitespeedSettings,
  'Other': GenericWhatsappSettings,
  'None': () => <div className="text-slate-500 italic p-4">No WhatsApp integration selected.</div>
};

export const SettingsTab: React.FC<{ user: UserProfile }> = ({ user }) => {
    // --- STATE MANAGEMENT ---
    const [platform, setPlatform] = useState(user.integrations?.ecommercePlatform || 'None');
    const [platformConfig, setPlatformConfig] = useState(user.integrations?.platformConfig || {});
    const [fulfillmentDelay, setFulfillmentDelay] = useState<number>(user.integrations?.fulfillmentDelay || 0);

    const [whatsapp, setWhatsapp] = useState(user.integrations?.whatsappProvider || 'None');
    const [whatsappConfig, setWhatsappConfig] = useState(user.integrations?.whatsappConfig || {});

    const [googleConnected, setGoogleConnected] = useState(user.integrations?.googleConnected || false);
    
    const [saving, setSaving] = useState(false);

    // --- SAFE SWITCHING LOGIC ---
    // If user switches back to the originally saved platform, restore its config.
    const handlePlatformChange = (newPlatform: string) => {
        setPlatform(newPlatform);
        if (newPlatform === user.integrations?.ecommercePlatform) {
            setPlatformConfig(user.integrations?.platformConfig || {});
        } else {
            setPlatformConfig({});
        }
    };

    const handleWhatsappChange = (newProvider: string) => {
        setWhatsapp(newProvider);
        if (newProvider === user.integrations?.whatsappProvider) {
            setWhatsappConfig(user.integrations?.whatsappConfig || {});
        } else {
            setWhatsappConfig({});
        }
    };

    // --- SAVE HANDLER ---
    const handleSave = async () => {
        setSaving(true);
        try {
            // 1. Fetch current config first to avoid deleting hidden fields (like googleFolderId)
            const { data: currentProfile } = await supabase
                .from('profiles')
                .select('integrations')
                .eq('id', user.id)
                .single();
            
            const existingIntegrations = currentProfile?.integrations || {};

            // 2. Merge updates
            const updates = {
                ...existingIntegrations,
                ecommercePlatform: platform,
                platformConfig: platformConfig,
                fulfillmentDelay: fulfillmentDelay,
                whatsappProvider: whatsapp,
                whatsappConfig: whatsappConfig,
                googleConnected: googleConnected
            };

            const { error } = await supabase.from('profiles').update({
                integrations: updates
            }).eq('id', user.id);

            if (error) throw error;
            alert("Settings saved successfully!");
            // Reload page to refresh user prop might be good, but for now alert is enough
        } catch (e) {
            console.error(e);
            alert("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    // --- DISCONNECT HANDLERS ---
    const disconnectPlatform = async () => {
        if(!confirm("Are you sure you want to disconnect your E-Commerce store?")) return;
        setPlatform('None');
        setPlatformConfig({});
        // Trigger save immediately to confirm action
        await saveSpecificUpdate({ ecommercePlatform: 'None', platformConfig: {} });
    };

    const disconnectWhatsapp = async () => {
        if(!confirm("Are you sure you want to disconnect WhatsApp?")) return;
        setWhatsapp('None');
        setWhatsappConfig({});
        // Trigger save immediately to confirm action
        await saveSpecificUpdate({ whatsappProvider: 'None', whatsappConfig: {} });
    };

    // Helper for immediate disconnect saves
    const saveSpecificUpdate = async (patch: any) => {
        setSaving(true);
        try {
            const { data: currentProfile } = await supabase.from('profiles').select('integrations').eq('id', user.id).single();
            const updates = { ...currentProfile?.integrations, ...patch };
            await supabase.from('profiles').update({ integrations: updates }).eq('id', user.id);
            alert("Disconnected successfully.");
        } catch(e) { alert("Error disconnecting."); } 
        finally { setSaving(false); }
    };

    const ActivePlatformComponent = PLATFORMS[platform] || GenericPlatformSettings;
    const ActiveWhatsappComponent = WHATSAPP_PROVIDERS[whatsapp] || GenericWhatsappSettings;

    return (
        <div className="max-w-5xl mx-auto pb-20 space-y-8">
            {/* HEADER */}
            <div className="flex justify-between items-center sticky top-0 z-20 bg-gray-50/95 backdrop-blur py-4 border-b border-gray-200">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">System Configuration</h2>
                    <p className="text-slate-500 text-sm">Manage your integrations and storage preferences</p>
                </div>
                <button 
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm transition-all"
                >
                    {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>}
                    Save All Changes
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* --- 1. E-COMMERCE SECTION --- */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <LayoutDashboard className="text-blue-600" size={24}/>
                                <h3 className="text-lg font-bold text-slate-800">E-Commerce Platform</h3>
                            </div>
                            {platform !== 'None' && (
                                <button onClick={disconnectPlatform} className="text-red-500 hover:text-red-700 text-xs font-medium flex items-center gap-1 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100">
                                    <Trash2 size={12}/> Disconnect
                                </button>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Platform</label>
                                <select 
                                    value={platform}
                                    onChange={(e) => handlePlatformChange(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="None">Select Platform...</option>
                                    {Object.keys(PLATFORMS).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            {/* DYNAMIC PLATFORM SETTINGS */}
                            {platform !== 'None' && (
                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                    <ActivePlatformComponent 
                                        config={platformConfig} 
                                        onChange={(updates: any) => setPlatformConfig({ ...platformConfig, ...updates })}
                                    />
                                    
                                    {/* FULFILLMENT DELAY */}
                                    <div className="pt-4 mt-4 border-t border-slate-100">
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Fulfillment Delay (Minutes)</label>
                                        <input 
                                            type="number"
                                            min="0"
                                            value={fulfillmentDelay}
                                            onChange={(e) => setFulfillmentDelay(parseInt(e.target.value))}
                                            className="w-full p-2.5 border rounded-lg"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Wait time before marking order as fulfilled.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- 2. WHATSAPP & GOOGLE SECTION --- */}
                <div className="space-y-6">
                    
                    {/* WHATSAPP CARD */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <Users className="text-green-600" size={24}/>
                                <h3 className="text-lg font-bold text-slate-800">WhatsApp Notifications</h3>
                            </div>
                            {whatsapp !== 'None' && (
                                <button onClick={disconnectWhatsapp} className="text-red-500 hover:text-red-700 text-xs font-medium flex items-center gap-1 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100">
                                    <Trash2 size={12}/> Disconnect
                                </button>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Provider</label>
                                <select 
                                    value={whatsapp}
                                    onChange={(e) => handleWhatsappChange(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg bg-slate-50 focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    <option value="None">Disable Notifications</option>
                                    {Object.keys(WHATSAPP_PROVIDERS).filter(k => k !== 'None').map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            {/* DYNAMIC WHATSAPP SETTINGS */}
                            {whatsapp !== 'None' && (
                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                    <ActiveWhatsappComponent 
                                        config={whatsappConfig} 
                                        onChange={(updates: any) => setWhatsappConfig({ ...whatsappConfig, ...updates })}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* GOOGLE DRIVE CARD */}
                    <GoogleDriveSettings 
                        userId={user.id} 
                        isConnected={googleConnected} 
                        onStatusChange={setGoogleConnected} 
                    />
<SecuritySettings />
                </div>
            </div>
        </div>
    );
};
